import React, { use, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import InputField from "./core-components/InputField.tsx";
import { useSelector } from "react-redux";
import { translations } from "../../utils/translations.js";
import { useAuth } from "../../hooks/useAuth.jsx";
import { login, sendResetToken } from "../../services/authService.js";
import { ToastContainer, toast } from "react-toastify";
import { roles } from "../../utils/constants.ts";
import Loader from "./core-components/Loader.tsx";

// Styled Components
const Container = styled.div`
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
  position: absolute;
  overflow: hidden;
  width: 768px;
  max-width: 70%;
  min-height: 480px;
  top: 15%;

  @media (max-width: 768px) {
    width: 300px;
  }
`;

const FormContainer = styled.div`
  position: absolute;
  width: 63%;
  top: 0;
  height: 100%;
  transition: all 0.6s ease-in-out;
  @media (max-width: 768px) {
    position: static;
    width: 100%;
    padding-top: 20px;
  }
`;
const SignInContainer = styled.div`
  left: 0;
  height: 90%;
  width: 80%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 50%;
  height: 100%;
  overflow: hidden;

  z-index: 100;
  @media (max-width: 768px) {
    display: none;
  }
`;

const Overlay = styled.div`
  background: linear-gradient(
    to right,
    var(--fill-green),
    var(--tertiary-green)
  );
  color: #ffffff;
  position: relative;
  left: -100%;
  height: 100%;
  width: 200%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const OverlayPanel = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  top: 0;
  height: 100%;
  width: 50%;
  left: 50%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Button = styled.button`
  border-radius: 20px;
  border: 1px solid var(--primary-green);
  background-color: var(--secondary-green);
  color: #ffffff;
  font-size: 12px;
  font-weight: bold;
  padding: 12px 45px;
  letter-spacing: 1px;
  text-transform: uppercase;

  cursor: pointer;
`;

const ButtonReset = styled.button`
  background: transparent;
  color: var(--primary-brown);
  border: none;

  cursor: pointer;
`;

const PasswordVisibilityButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  background: none;
  border: none;
  color: var(--primary-brown);
  font-size: 16px;
  cursor: pointer;
`;
const GoButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-brown);
  font-size: 16px;
  cursor: pointer;

  @media (min-width: 768px) {
    display: none;
  }
`;
const notifySucces = (msg) =>
  toast.success(msg, {
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    theme: "light",
  });

const notify = (msg) =>
  toast.error(msg, {
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    theme: "light",
  });

const LoginModal = () => {
  const { loginSucces } = useAuth();

  const languageCode = useSelector((state: any) => state.settings.languageCode);

  // const [hide, setHide] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // const togglePasswordVisibility = () => setHide(!hide);
  const {
    data: token,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["login", email, password],
    queryFn: () => login({ email, password }),
    enabled: false, // Nu rulează automat
    retry: false,
  });

  useEffect(() => {
    if (isError) {
      setIsLoading(false);
    }
  }, [isError]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    console.log("Login data", { email, password });
    refetch();
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!email || email === "") {
      notify(translations[languageCode]["login-reset-email"]);
      return;
    } else {
      try {
        setIsLoading(true);
        await sendResetToken(email);
        console.log("Email sent");
        notifySucces(translations[languageCode]["login-reset-success"]);
      } catch (error) {
        setIsLoading(false);
        notify(translations[languageCode]["reset-error"]);
        console.error("Error sending email", error);
      }
    }
  };

  useEffect(() => {
    if (token) {
      loginSucces(token, roles.CLIENT, email);
      setEmail("");
      setPassword("");
    } else if (isError) {
      notify(translations[languageCode]["login-error"]);
    }
  }, [token, email, loginSucces, isError]);
  return (
    <Container>
      <FormContainer>
        <SignInContainer>
          <h1> {translations[languageCode]["login-title"]}</h1>
          <div>
            <InputField
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isRequired
              placeholder="email@example.com"
              label="Email"
              height="36px"
            />
          </div>
          <div style={{ position: "relative" }}>
            <InputField
              name="password"
              type={"password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isRequired
              label="Password"
              placeholder="Pass123!@#"
              height="36px"
            />
            {/* <PasswordVisibilityButton onClick={togglePasswordVisibility}>
              {hide ? "Show" : "Hide"}
            </PasswordVisibilityButton> */}
          </div>
          <ButtonReset onClick={handleReset}>
            {translations[languageCode]["reset-pass-log-modal"]}
          </ButtonReset>
          {isLoading ? (
            <Loader />
          ) : (
            <Button onClick={handleSubmit}>
              {translations[languageCode]["login-button"]}
            </Button>
          )}

          {/* <GoButton onClick={() => (window.location.href = "/register")}>
            {translations[languageCode]["login-register-message"]}
          </GoButton> */}
        </SignInContainer>
      </FormContainer>

      <OverlayContainer>
        <Overlay>
          <OverlayPanel>
            <h1>Hello!</h1>

            <p>{translations[languageCode]["login-register-message"]}</p>
            <Button onClick={() => (window.location.href = "/register")}>
              {translations[languageCode]["go-register"]}
            </Button>
          </OverlayPanel>
        </Overlay>
      </OverlayContainer>
      <ToastContainer />
    </Container>
  );
};

export default LoginModal;
